import { create } from 'zustand';
import type { AiModuleFillingScenario } from '../@types/types';
import type { OneButtonUserCapabilityResponse } from '../@types/oneButtonCapability';
import oneButtonCapabilityService from '../services/oneButtonServices/capabilityService';
import { DEFAULT_PRICING_PRODUCTS_ENABLED } from '../helpers/financeSelectors';
import { normalizeFinanceGridPartner } from '../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance/financePartnerNormalize';

/** Must match ThemeContext `theme` key; `obd-dashboard-theme` read once for migration. */
function readSyncedAppTheme(): 'dark' | 'light' {
    if (typeof window === 'undefined') return 'dark';
    try {
        const t = localStorage.getItem('theme');
        if (t === 'light' || t === 'dark') return t;
        const legacy = localStorage.getItem('obd-dashboard-theme');
        if (legacy === 'light' || legacy === 'dark') return legacy;
    } catch {
        /* ignore */
    }
    return 'dark';
}
import type {
    RetrieveSystemDesignResponse,
    SystemDesignLossesResponse,
    RetrievePriceQuoteResponse,
    GetDesignImageResponse,
} from '../services/oneButtonServices/designServices';
import type { ProductTypeAdder } from '../services/oneButtonServices/quoteServices';

/** Common constants used across the app */
export const COMMON_CONSTANTS = {
    SolarProductName: 'Solar',
    RoofProductName: 'Roof',
    RRRProductName: 'RRR',
    BatteryProductName: 'Battery',
    SunnovaPartnerName: 'Sunnova',
    IGSPartnerName: 'IGS',
    GoodLeapPartnerName: 'Good Leap',
    LightreachPartnerName: "Lightreach",
    SunlightPartnerName: 'Sunlight',
    EnfinPartnerName:'Enfin',
    CashPartnerName:'Cash',
    RoofOnlyProductName: 'Roof Only',
    BatteryOnlyProductName: 'Battery Only',
    RRProduct: 'RR'
};

export type FinancingProduct =
    | 'Solar'
    | 'Roof'
    | 'Solar + Roof'
    | 'Battery'
    | 'Battery + Roof'
    | 'Solar + Battery'
    | 'RR';

/** A tree-cutting sub-scenario tied to a parent AI design */
export interface TreeCutSubScenario {
    id: string;
    parentDesignId: string;
    scenarioIndex: number;
    treeLabel: string | null;
    treeIds: string[];
    cutCost: number | null;
    modules: number;
    roofCount: number;
    productionW: number;
    systemKw: number;
    gainW: number;
    isMultiTree: boolean;
    /** Top-down capture for this tree-cutting scenario (ghosted trees on map). */
    snapshotUrl?: string;
}

/** Saved AI design from oneDRAW Max Fill results */
export interface SavedAIDesign {
    aiDesignId: string;
    layoutKey: string;
    label: string;
    /** Optional category id for dashboard cards (e.g. "Max Production with Best Roofs") */
    categoryId?: string;
    productionW: number;
    systemKw: number;
    modules: number;
    roofCount: number;
    roofFaceIds: string[];
    treeCount: number;
    createdAt: number;
    treeScenarios: AiModuleFillingScenario[];
    treeCutSubScenarios: TreeCutSubScenario[];
    snapshotUrl?: string;
}

export interface PriceCell {
    monthly: number;
    apr: number;
    term: number;
    escalator?: number | null;
    /** When true, show "RECOMMENDATION" label above the price */
    recommended?: boolean;
    /** Finance method (Loan, Lease, etc.) */
    financeMethod?: string;
    /** GoodLeap recommended: loan amount, EPC (PPA/lease), or cash total — shown in grid only for that partner */
    purchasePrice?: number | null;
    payload?: any;
    ExcelOffer?: any;
    /** Pricing API `Recommended: true` on the offer used for this cell (not the grid star). */
    apiRecommended?: boolean;
    tier?:string;
}

/** Keys can be product names (Solar, Roof, ...) or finance method labels (Loan, Lease, ...) from API */
export interface FinancePartnerRow {
    partner: string;
    prices: Record<string, PriceCell | null>;
}

/** When set, grid shows these columns (finance methods) instead of FINANCING_PRODUCTS */
export type FinanceMethodColumn = string;

/** Design snapshot passed to the pricing API when user selects a layout card */
export interface SelectedDesignForPricing {
    /** Unique per card: categoryId + layoutKey so the same layout in different categories is distinct */
    designId: string;
    /** Layout category (e.g. "Max Production with Best Roofs", "Aesthetic") */
    categoryId: string;
    systemSizeKw: number;
    estimatedAnnualProductionKwh: number;
    /** Layout annual production (kWh/yr) when set from the editor; legacy name. */
    productionW?: number;
    modules?: number;
    roofCount?: number;
    layoutKey?: string;
    /** Tree-cutting scenario: details for selected tree(s) shown in pricing modal. */
    treeDetails?: Array<{
        treeId: string;
        label?: string;
        dbh?: string;
        height?: string;
        stump?: string;
        type?: string;
    }>;
}

/** Roof/square overrides from Contract RetrievePriceQuote API + roof details UI */
export interface PriceQuoteOverrides {
    roofSquareOverride: number | null;
    normalSlopeOverride: number | null;
    lowSlopeOverride: number | null;
    /** Price per square foot (roof); used for IKO Dynasty eligibility ($8.89 min) */
    pricePerSqFt?: number | null;
    /** Selected shingle texture id (e.g. IKODYNAB) */
    selectedShingleId?: string | null;
    /** Display name for roof card (e.g. Atlantic Blue) */
    selectedShingleAlt?: string | null;
    /** Read-only from API when available */
    minSolarRate?: number | null;
    proposedSolarRate?: number | null;
    /** For RRR systems */
    rrSystemSize?: number | null;
}

/** Converted Hover roof estimate (Angular `openHoverJob` afterClose result). */
export interface SelectedHoverEstimate {
    hoverEstimateID: string;
    lowSq: number;
    normalSq: number;
    /** $/sq price — API estimate `Low_Slope_Override` (Angular `lowSqOverride` → Low_Slope_Price_Override). */
    lowSqOverride: number;
    /** $/sq price — API estimate `Normal_Slope_Override` (Angular `normalSqOverride` → Normal_Slope_Price_Override). */
    normalSqOverride: number;
    normalSlopeFloorRate: number;
    lowSlopeFloorRate: number;
    wastageSq: number;
    pricing: number;
    shingleType: string;
    shingleTypeName?: string | null;
    shingleManufacturer?: string | null;
    shingleProductCode?: string | null;
    adders?: Array<{
        AdderSelect: string;
        description?: string;
        AdderQuantity: number;
        UnitPrice: number;
        AdderCost: number;
    }>;
}

/** Pending Generate Quote open request after Hover Convert (FinancingGridCard selectedQuote shape). */
export type HoverPendingGenerateQuote = {
    product: string;
    partner?: string;
    methord?: string | null;
    price?: number | null;
    ExcelOffer?: any;
    payload?: any;
    products?: HoverPendingGenerateQuote[];
};

/** Lines shown under Roof / Battery on the merged System & design card */
export interface DashboardQuotesSummaryLines {
    selectedQuoteLine: string | null;
    signedContractLine: string | null;
}

export interface DesignHistoryItem {
  Design_Id: number;
  Design_Name: string;
  Design_Json: string;
  Active_Ind: boolean;
  User_Modified_Id: string;
  User_Modified_Timestamp: string;
}
/**
 * Rows from Contract API `GET .../getLoanStatus?opportunityId=` (Trinity `GenerateQuoteService.getLoanStatus` /
 * Angular `updateChangeOrderDetails` / action-button-group). Shape varies by partner; flags are read across the dashboard.
 */
export interface ChangeOrderLoanStatusRow {
    Finance_Partner_Name?: string;
    finance_Partner_Name?: string;
    application_Status?: string;
    Is_Change_Order?: boolean;
    is_Change_Order?: boolean;
    Is_Utility_Updated?: boolean;
    is_Utility_Updated?: boolean;
    Is_System_Design_Updated?: boolean;
    is_System_Design_Updated?: boolean;
    [key: string]: unknown;
}

export interface OneButtonState {
    salesForceId: string | null;
    setSalesForceId: (id: string | null | undefined) => void;

    opportunityID: string | null; // Opportunity ID Number from Salesforce
    setOpportunityID: (id: string | null | undefined) => void;
    /** Clears all opportunity-scoped OneButton state (pricing grid, modals, cached API responses, etc.). */

    opportunityDetails: any | null;
    setOpportunityDetails: (details: any | null) => void;

    /** From sites API (`siteService.getOpportunity`); not present on oneButton opportunity detail. */
    energyCommunityStatus: string | null;
    setEnergyCommunityStatus: (status: string | null | undefined) => void;

    /** When opportunity detail API fails (e.g. 400), show in dashboard top-right */
    opportunityLoadError: string | null;
    setOpportunityLoadError: (v: string | null) => void;

    /** Global Opportunity Information page loader — true while critical dashboard APIs are in flight. */
    isOpportunityInitializing: boolean;
    opportunityInitializationError: string | null;
    opportunityInitializationPhase: string | null;
    /** Salesforce id for which the last initialization run completed (success or graceful partial failure). */
    opportunityInitializedSfid: string | null;
    /** Monotonic token; stale init runs exit when this changes (opportunity switch / refresh). */
    opportunityInitRequestId: number;
    /** When true, {@link OpportunityDetailsCard} defers detail/utility/master fetches to the orchestrator. */
    opportunityInitManagedByOrchestrator: boolean;
    beginOpportunityInitialization: (salesForceId: string) => number;
    completeOpportunityInitialization: (salesForceId: string, error?: string | null) => void;
    resetOpportunityInitialization: () => void;

    productType: string | null;
    setProductType: (pt: string | null) => void;

    solarUtilityData: any | null;
    setSolarUtilityData: (utility: any | null) => void;

    /** Display name for utility company (e.g. "Eversource (Formerly CL&P)") for the Utility Details card */
    utilityCompanyName: string | null;
    setUtilityCompanyName: (name: string | null) => void;

    /** True when Utility Details has local edits not yet persisted (blocks solar quote until saved). */
    utilityDetailsDirty: boolean;
    setUtilityDetailsDirty: (dirty: boolean) => void;

    /** oneButton Design History. */
    designHistory: DesignHistoryItem[] | null;
    setDesignHistory: (v: DesignHistoryItem[] | null) => void;

    /** When set, financing pricing API uses this instead of solarUtilityData for systemSize / estimatedAnnualProduction */
    selectedDesignForPricing: SelectedDesignForPricing | null;
    setSelectedDesignForPricing: (design: SelectedDesignForPricing | null) => void;

    /** True while pricing is being fetched after user selects a design (show spinner on selection button) */
    pricingSelectionLoading: boolean;
    setPricingSelectionLoading: (loading: boolean) => void;

    /**
     * Number of Pricing API calls currently in flight, from any caller (financing grid,
     * design card EPC cells, tree-cut scenarios). Lets the financing section show a loading
     * state for requests it did not start itself.
     */
    pricingRequestsInFlight: number;
    adjustPricingRequestsInFlight: (delta: number) => void;

    financingGrid: FinancePartnerRow[];
    setFinancingGrid: (grid: FinancePartnerRow[]) => void;
    /** When from new pricing API: column headers to show (e.g. Loan, Lease, Lease (Prod Guarantee)) */
    financeMethodColumns: FinanceMethodColumn[];
    setFinanceMethodColumns: (cols: FinanceMethodColumn[]) => void;

    selectedFinanceCells: Set<string>;
    toggleFinanceCell: (key: string) => void;
    addFinanceCell: (key: string) => void;

    /** Quote / contract one-liners for System & design summary (synced from Quotes card) */
    dashboardQuotesSummaryLines: DashboardQuotesSummaryLines;
    setDashboardQuotesSummaryLines: (lines: DashboardQuotesSummaryLines | null) => void;
    /** Contract API product-type adders, loaded when Quotes opens and used by the Adders popup. */
    productTypeAdders: ProductTypeAdder[];
    setProductTypeAdders: (rows: ProductTypeAdder[] | null) => void;

    /**
     * Latest `getLoanStatus` (by opportunity) payload — updated whenever `contractGetLoanStatusByOpportunity` succeeds.
     * Cleared when the salesforce context or opportunity id changes.
     */
    changeOrderDetails: ChangeOrderLoanStatusRow[] | null;
    setChangeOrderDetails: (rows: ChangeOrderLoanStatusRow[] | null) => void;

    checkListDetails:any[] | null;
    setCheckListDetails: (rows: any[] | null) => void;

    theme: 'dark' | 'light';
    setTheme: (theme: 'dark' | 'light') => void;

    /** Saved AI designs from oneDRAW Max Fill */
    savedAIDesigns: SavedAIDesign[];
    setSavedAIDesigns: (designs: SavedAIDesign[]) => void;
    addAIDesign: (design: SavedAIDesign) => void;
    removeAIDesign: (aiDesignId: string) => void;
    updateAIDesign: (aiDesignId: string, patch: Partial<SavedAIDesign>) => void;

    /** Currently active (selected) AI design — only one at a time */
    activeAIDesignId: string | null;
    setActiveAIDesignId: (id: string | null) => void;

    /** Active sub-scenario within the active design (tree-cutting selection) */
    activeSubScenarioId: string | null;
    setActiveSubScenarioId: (id: string | null) => void;

    /** Add a tree-cut sub-scenario to a saved AI design */
    addTreeCutSubScenario: (aiDesignId: string, sub: TreeCutSubScenario) => void;
    removeTreeCutSubScenario: (aiDesignId: string, subId: string, callback?: (newWishlist: SavedAIDesign[]) => void) => void;

    /** Roof/square overrides from RetrievePriceQuote (RoofSquareOverride, Normal_Slope_Override, Low_Slope_Override) */
    priceQuoteOverrides: PriceQuoteOverrides | null;
    setPriceQuoteOverrides: (v: PriceQuoteOverrides | null) => void;

    /** From Design API: RetrieveSystemDesign (DesignArray, Roof for roof squares) */
    systemDesign: RetrieveSystemDesignResponse | null;
    setSystemDesign: (v: RetrieveSystemDesignResponse | null) => void;

    /** True while ProductionsCard system-design refresh is in flight (finalize flow waits on this). */
    systemDesignRefreshing: boolean;
    setSystemDesignRefreshing: (refreshing: boolean) => void;

    /** Bump after finalize / manual refresh so dashboard re-fetches system design metrics. */
    systemDesignRefreshTrigger: number;
    setSystemDesignRefreshTrigger: (v: number) => void;

    /** From Design API: SystemDesignLosses */
    systemDesignLosses: SystemDesignLossesResponse | null;
    setSystemDesignLosses: (v: SystemDesignLossesResponse | null) => void;

    /** From Design API: GetDesignImage (Id, Name for design screenshot) */
    designImage: GetDesignImageResponse | null;
    setDesignImage: (v: GetDesignImageResponse | null) => void;
    
     /** From Contract API: RetrievePriceQuote */
    priceQuoteDetails: RetrievePriceQuoteResponse | null;
    setPriceQuoteDetails: (v: RetrievePriceQuoteResponse | null) => void;

    /** Background prefetch of RetrievePriceQuote for System & Design card metrics */
    systemDesignQuotesLoading: boolean;
    setSystemDesignQuotesLoading: (v: boolean) => void;

    /** Global master data for opportunity info steps */
    opportunityinfoMasterData: any | null;
    setOpportunityinfoMasterData: (data: any | null) => void;

    /** Master data for equipment (modules, inverters, racking, etc.) */
    equipmentMasterData: any | null;
    setEquipmentMasterData: (data: any | null) => void;

    /** Cached battery models for current manufacturer */
    batteryModelData: any[] | null;
    setBatteryModelData: (data: any[] | null) => void;

    /** Property title information from Black Knight API */
    titleInfo: any | null;
    setTitleInfo: (info: any | null) => void;

    /** List of valid products filtered from master data */
    productTypeList: any[];
    setProductTypeList: (list: any[]) => void;

    /** List of valid finance partners filtered from master data */
    financePartnersList: any[];
    setFinancePartnersList: (list: any[]) => void;

    /** Finance Partner Sync flow: per-partner checklist status (Lead, Lead System, Utility, Contact, Title Check, Credit Check) */
    financePartnerSyncStatus: Record<string, PartnerSyncChecklist>;
    setFinancePartnerSyncStatus: (
        partner: string,
        status: Partial<PartnerSyncChecklist>,
        productTypeName?: string | null,
    ) => void;
    /** When true, show the Finance Partner Sync pop-up (e.g. when Lead/Lead System/Utility sync missing) */
    financePartnerSyncModalOpen: boolean;
    setFinancePartnerSyncModalOpen: (open: boolean) => void;
    /** Lead Sync form (contacts + Account id sync): open when user clicks Create/Sync with existing lead */
    leadSyncFormOpen: boolean;
    leadSyncFormAction: 'create' | 'sync_existing' | null;
    leadSyncFormPartnerName: string | null;
    /** Product line that opened Lead Sync (e.g. Solar, Battery). Empty means show all product types. */
    leadSyncFormProductName: string | null;
    /** When true (e.g. opened from Products hub Good Leap Application), hide "Create New Finance Partner Lead" */
    leadSyncFormHideCreateLead: boolean;
    setLeadSyncFormOpen: (
        open: boolean,
        action?: 'create' | 'sync_existing' | null,
        partnerName?: string | null,
        options?: { hideCreateLead?: boolean; productName?: string | null },
    ) => void;
    /** IGS-specific Lead Sync Modal */
    igsLeadSyncModalOpen: boolean;
    setIgsLeadSyncModalOpen: (open: boolean, productName?: string | null) => void;

    /** Credit Check Modal */
    creditCheckModalOpen: boolean;
    creditCheckPartnerName: string | null;
    setCreditCheckModalOpen: (open: boolean, partnerName?: string | null) => void;

    financePartnerRawSyncStatuses: any[];
    setFinancePartnerRawSyncStatuses: (statuses: any[]) => void;

    contractCreationStatuses: any[];
    setContractCreationStatuses: (statuses: any[]) => void;

    /** In-memory storage for SFDC sync errors performed from the agreement page */
    sfdcSyncErrors: Record<string, string>;
    setSfdcSyncError: (step: string, error: string | null) => void;
    clearSfdcSyncErrors: () => void;

    /** True when background tasks are running sequentially after modal close */
    isBackgroundSyncing: boolean;
    setIsBackgroundSyncing: (isSyncing: boolean) => void;

    /** Commerce hub modals (Products / Quotes / Agreements) — shared with sticky footer agent */
    commerceHubProductsOpen: boolean;
    commerceHubQuotesOpen: boolean;
    commerceHubAgreementsOpen: boolean;
    commerceHubStripeOpen: boolean;
    setCommerceHubProductsOpen: (open: boolean, opts?: { keepQuotesOpen?: boolean }) => void;
    setCommerceHubQuotesOpen: (open: boolean) => void;
    setCommerceHubAgreementsOpen: (open: boolean) => void;
    setCommerceHubStripeOpen: (open: boolean) => void;
    /** PCI: oneDRAW design/losses not finalized — opportunity product partner edits are locked. */
    pciLockOpportunityProducts: boolean;
    setPciLockOpportunityProducts: (locked: boolean) => void;

    /** Contacts modal — shared between dashboard footer and main panels */
    dashboardContactsModalOpen: boolean;
    setDashboardContactsModalOpen: (open: boolean) => void;
    /** Open contacts modal directly to new-contact editor (V2 header Add Contact). */
    dashboardContactsOpenNewContact: boolean;
    setDashboardContactsOpenNewContact: (open: boolean) => void;
    /** Bump after title sync so open contacts UI re-reads OnTitleCheck from store. */
    contactsTitleSyncRevision: number;
    notifyContactsTitleSynced: () => void;
    /** Sales person modal — opened from missing-item Fill in / header. */
    dashboardSalesPersonModalOpen: boolean;
    setDashboardSalesPersonModalOpen: (open: boolean) => void;

    /** Sales-rep checklist (Trinity `app-salesrep-checklist`) — store-driven modal. */
    salesRepChecklistModalOpen: boolean;
    setSalesRepChecklistModalOpen: (open: boolean) => void;
    /** Answered / total from GetChecklist, used by the Status & Milestones Checklist card. */
    salesRepChecklistProgress: { answered: number; total: number; percentage: number } | null;
    setSalesRepChecklistProgress: (progress: { answered: number; total: number; percentage: number } | null) => void;
    /** Tone data from GetChecklistResponseTone (opportunityId, responseTone, responseColorCode). */
    checklistResponseTone: { opportunityId?: number; responseTone?: string; responseColorCode?: string } | null;
    setChecklistResponseTone: (tone: { opportunityId?: number; responseTone?: string; responseColorCode?: string } | null) => void;

    /** Documents modal (Trinity `app-documents`) — store-driven modal. */
    documentsModalOpen: boolean;
    setDocumentsModalOpen: (open: boolean) => void;

    /**
     * Hover Job ID from Contract `GET HoverJobID` (Angular `masterService.hoverJobId`).
     * When set with Enable_Hover_Job + Roof/RoofOnly, Roof Design opens Hover Roof Estimates.
     */
    hoverJobId: string | null;
    setHoverJobId: (id: string | null) => void;
    /** Last Convert selection from HoverRoofEstimatesModal — used by Generate Quote payload. */
    selectedHoverEstimate: SelectedHoverEstimate | null;
    setSelectedHoverEstimate: (v: SelectedHoverEstimate | null) => void;
    /**
     * After Hover Convert: pending Generate Quote selection for FinancingGridCard / QuotesContractsCard.
     * Cleared when consumed by a host that opens GenerateQuoteModal.
     */
    hoverPendingGenerateQuote: HoverPendingGenerateQuote | null;
    setHoverPendingGenerateQuote: (v: HoverPendingGenerateQuote | null) => void;

    /** Roof quote details — written by RoofQuoteDetails, read by GenerateQuoteModal */
    roofDetails: any | null;
    updateRoofDetails: (data: any) => void;
    /** Standalone RR (R&R) quote details — written by RRQuoteDetails, read by GenerateQuoteModal */
    rrDetails: any | null;
    updateRRDetails: (data: any) => void;
    /** Battery quote details — written by BatteryQuoteDetails, read by GenerateQuoteModal */
    batteryDetails: any | null;
    updateBatteryDetails: (data: any) => void;
    /** Trigger to refresh quotes in QuotesContractsCard when a new quote is generated from other components */
    quotesRefreshTrigger: number;
    setQuotesRefreshTrigger: (v: number) => void;

    /** Bump to skip pricing cache and call getPricing (system design refresh, financing grid refresh). */
    pricingNetworkRefreshTrigger: number;
    setPricingNetworkRefreshTrigger: (v: number) => void;

    /** Per-product toggles for inline Financing Options pricing API (default off). */
    pricingProductsEnabled: Record<'Solar' | 'Roof' | 'Battery', boolean>;
    togglePricingProductEnabled: (product: 'Solar' | 'Roof' | 'Battery') => void;

    /** Latest inline pricing design fingerprint (for per-product cache indicators). */
    pricingDesignFingerprint: string | null;

    /** When set, pricing modal re-runs pricing (AI scenario $); does not refresh inline Financing Options. */
    pricingModalFetchTrigger: number | null;
    setPricingModalFetchTrigger: (v: number | null) => void;

    /** Bump to reload AgreementsModal / embedded Agreements from RetrieveContract after contract or CO writes */
    agreementsRefreshTrigger: number;
    setAgreementsRefreshTrigger: (v: number) => void;

    /**
     * After GoodLeap `UpdateChangeOrder` from Quotes: when Agreements load completes, expand the agreement row
     * whose numeric `QuoteId` matches this value (then cleared by AgreementsModal).
     */
    agreementsPendingExpandQuoteId: string | null;
    setAgreementsPendingExpandQuoteId: (id: string | null) => void;

    /** Bump to reload finance selection (GetFinanceSelection) after product settings save */
    financeSelectionRefreshTrigger: number;
    setFinanceSelectionRefreshTrigger: (v: number) => void;

    /** UAM capabilities for oneBUTTON (global, fetched after auth) */
    onebuttonCapabilities: OneButtonUserCapabilityResponse | null;
    fetchOnebuttonCapabilities: (force?: boolean) => Promise<void>;
}

/** Per-partner sync checklist for Finance Partner Sync flow (same form for each partner) */
export interface PartnerSyncChecklist {
    /** Lead Sync step: 'create' | 'sync_existing' | null */
    leadSyncAction: 'create' | 'sync_existing' | null;
    lead: boolean;
    leadSystem: boolean;
    utility: boolean;
    contact: boolean;
    titleCheck: boolean;
    creditCheck: boolean;
}

export const useOneButtonStore = create<OneButtonState>((set) => ({
    salesForceId: null,
    opportunityID: null, // Opportunity ID Number from Salesforce

    setOpportunityID: (id) =>
        set((state) => {
            const next = id ?? null;
            if (next === state.opportunityID) return state;
            return { opportunityID: next, changeOrderDetails: null, agreementsPendingExpandQuoteId: null };
        }),
    setSalesForceId: (id) =>
        set((state) => {
            const normalizeSfid = (v: string | null | undefined) => {
                if (v == null) return null;
                const t = String(v).trim();
                return t === '' || t === '—' ? null : t;
            };
            const nextNorm = normalizeSfid(id);
            const prevNorm = normalizeSfid(state.salesForceId);
            // SitesTable + ThreeDViewer both set the same sfid on /sites → /configure navigation.
            // Skipping reset preserves in-flight opportunity initialization (loader + orchestrator).
            if (nextNorm !== null && nextNorm === prevNorm) {
                return state;
            }

            // Opportunity changed — clear any stale OneButton state so previous data cannot leak.
            return {
                salesForceId: id,
                opportunityID: null,
                opportunityDetails: null,
                energyCommunityStatus: null,
                opportunityLoadError: null,
                productType: null,
                solarUtilityData: null,
                utilityCompanyName: null,
                utilityDetailsDirty: false,

                selectedDesignForPricing: null,
                pricingSelectionLoading: false,
                pricingRequestsInFlight: 0,
                financingGrid: [],
                financeMethodColumns: [],
                selectedFinanceCells: new Set<string>(),
                dashboardQuotesSummaryLines: { selectedQuoteLine: null, signedContractLine: null },
                changeOrderDetails: null,

                savedAIDesigns: [],
                activeAIDesignId: null,
                activeSubScenarioId: null,

                priceQuoteOverrides: null,
                systemDesign: null,
                systemDesignRefreshing: false,
                systemDesignRefreshTrigger: 0,
                systemDesignLosses: null,
                designImage: null,
                priceQuoteDetails: null,
                systemDesignQuotesLoading: false,

                batteryModelData: null,
                titleInfo: null,

                financePartnerSyncStatus: {},
                financePartnerSyncModalOpen: false,
                leadSyncFormOpen: false,
                leadSyncFormAction: null,
                leadSyncFormPartnerName: null,
                leadSyncFormProductName: null,
                leadSyncFormHideCreateLead: false,
                igsLeadSyncModalOpen: false,
                creditCheckModalOpen: false,
                creditCheckPartnerName: null,
                financePartnerRawSyncStatuses: [],
                sfdcSyncErrors: {},
                isBackgroundSyncing: false,

                commerceHubProductsOpen: false,
                commerceHubQuotesOpen: false,
                commerceHubAgreementsOpen: false,
                commerceHubStripeOpen: false,
                pciLockOpportunityProducts: false,
                dashboardContactsModalOpen: false,
                dashboardContactsOpenNewContact: false,
                dashboardSalesPersonModalOpen: false,
                salesRepChecklistModalOpen: false,
                salesRepChecklistProgress: null,

                hoverJobId: null,
                selectedHoverEstimate: null,
                hoverPendingGenerateQuote: null,
                roofDetails: null,
                rrDetails: null,
                batteryDetails: null,
                quotesRefreshTrigger: 0,
                pricingNetworkRefreshTrigger: 0,
                pricingProductsEnabled: { ...DEFAULT_PRICING_PRODUCTS_ENABLED },
                pricingDesignFingerprint: null,
                pricingModalFetchTrigger: null,
                agreementsRefreshTrigger: 0,
                agreementsPendingExpandQuoteId: null,
                financeSelectionRefreshTrigger: 0,

                isOpportunityInitializing: false,
                opportunityInitializationError: null,
                opportunityInitializationPhase: null,
                opportunityInitializedSfid: null,
            };
        }),

    isOpportunityInitializing: false,
    opportunityInitializationError: null,
    opportunityInitializationPhase: null,
    opportunityInitializedSfid: null,
    opportunityInitRequestId: 0,
    opportunityInitManagedByOrchestrator: false,

    beginOpportunityInitialization: (salesForceId) => {
        const nextId = useOneButtonStore.getState().opportunityInitRequestId + 1;
        set({
            opportunityInitRequestId: nextId,
            isOpportunityInitializing: true,
            opportunityInitializationError: null,
            opportunityInitializationPhase: 'Loading opportunity information…',
            opportunityInitializedSfid: null,
            opportunityLoadError: null,
        });
        return nextId;
    },

    completeOpportunityInitialization: (salesForceId, error) =>
        set({
            isOpportunityInitializing: false,
            opportunityInitializationError: error ?? null,
            opportunityInitializationPhase: null,
            opportunityInitializedSfid: error ? null : salesForceId,
        }),

    resetOpportunityInitialization: () =>
        set({
            isOpportunityInitializing: false,
            opportunityInitializationError: null,
            opportunityInitializationPhase: null,
            opportunityInitializedSfid: null,
        }),

    opportunityDetails: null,
    energyCommunityStatus: null,
    setEnergyCommunityStatus: (status) =>
        set((state) => {
            const next =
                status == null || String(status).trim() === '' ? null : String(status).trim();
            if (next === state.energyCommunityStatus) return state;
            return { energyCommunityStatus: next };
        }),
    opportunityLoadError: null,
    setOpportunityLoadError: (v) => set({ opportunityLoadError: v ?? null }),
    productType: null,
    setProductType: (pt) => set({ productType: pt ?? null }),
    setOpportunityDetails: (details: any | null) => {
        set((state) => {
            let pt = "";
            let nextDetails = details ?? null;
            const ecStatus =
                state.energyCommunityStatus ??
                state.opportunityDetails?.EnergyCommunityStatus ??
                state.opportunityDetails?.energyCommunityStatus ??
                null;
            if (nextDetails && ecStatus != null && String(ecStatus).trim() !== '') {
                const incoming =
                    nextDetails.EnergyCommunityStatus ?? nextDetails.energyCommunityStatus;
                if (!String(incoming ?? '').trim()) {
                    nextDetails = { ...nextDetails, EnergyCommunityStatus: ecStatus };
                }
            }
            const oppInterest = nextDetails?.OpportunityInterest;
            if (oppInterest) {
                if (oppInterest.Solar === true) pt = 'S';
                if (oppInterest.Battery === true) pt = pt + 'B';
                if (oppInterest.Roof === true || oppInterest.RRR === true) pt = pt + 'R';
                if (oppInterest.BatteryOnly === true) pt = 'B';
                if (oppInterest.RoofOnly === true) pt = 'R';
            }
            return {
                opportunityDetails: nextDetails,
                productType: pt || null,
                utilityDetailsDirty: false,
            };
        });
    },

    solarUtilityData: null,
    setSolarUtilityData: (utility: any | null) => set({ solarUtilityData: utility ?? null }),

    utilityCompanyName: null,
    setUtilityCompanyName: (name: string | null) => set({ utilityCompanyName: name ?? null }),

    utilityDetailsDirty: false,
    setUtilityDetailsDirty: (dirty) => set({ utilityDetailsDirty: dirty }),

    selectedDesignForPricing: null,
    setSelectedDesignForPricing: (design) => set({ selectedDesignForPricing: design ?? null }),

    pricingSelectionLoading: false,
    setPricingSelectionLoading: (loading) => set({ pricingSelectionLoading: loading }),
    pricingRequestsInFlight: 0,
    adjustPricingRequestsInFlight: (delta) =>
        set((state) => ({
            pricingRequestsInFlight: Math.max(0, state.pricingRequestsInFlight + delta),
        })),

    financingGrid: [],
    setFinancingGrid: (grid) => set({ financingGrid: grid }),
    financeMethodColumns: [],
    setFinanceMethodColumns: (cols) => set({ financeMethodColumns: cols }),

    selectedFinanceCells: new Set<string>(),
    toggleFinanceCell: (key) =>
        set((state) => {
            const next = new Set(state.selectedFinanceCells);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return { selectedFinanceCells: next };
        }),
    addFinanceCell: (key) =>
        set((state) => {
            if (state.selectedFinanceCells.has(key)) return state;
            const next = new Set(state.selectedFinanceCells);
            next.add(key);
            return { selectedFinanceCells: next };
        }),

    dashboardQuotesSummaryLines: { selectedQuoteLine: null, signedContractLine: null },
    setDashboardQuotesSummaryLines: (lines) =>
        set({
            dashboardQuotesSummaryLines: lines ?? { selectedQuoteLine: null, signedContractLine: null },
        }),

    productTypeAdders: [],
    setProductTypeAdders: (rows) => set({ productTypeAdders: Array.isArray(rows) ? rows : [] }),

    changeOrderDetails: null,
    setChangeOrderDetails: (rows) => set({ changeOrderDetails: rows ?? null }),

    checkListDetails:null,
    setCheckListDetails: (rows: any[] | null) => set({ checkListDetails: rows ?? null }),


    theme: readSyncedAppTheme(),
    /** In-memory sync from ThemeContext only — persistence lives on `localStorage.theme`. */
    setTheme: (theme) => set({ theme }),

    savedAIDesigns: [],
    setSavedAIDesigns: (designs) => set({ savedAIDesigns: designs }),
    addAIDesign: (design) =>
        set((state) => ({
            savedAIDesigns: [...state.savedAIDesigns.filter((d) => d.aiDesignId !== design.aiDesignId), design],
        })),
    removeAIDesign: (aiDesignId) =>
        set((state) => ({
            savedAIDesigns: state.savedAIDesigns.filter((d) => d.aiDesignId !== aiDesignId),
            activeAIDesignId: state.activeAIDesignId === aiDesignId ? null : state.activeAIDesignId,
        })),
    updateAIDesign: (aiDesignId, patch) =>
        set((state) => ({
            savedAIDesigns: state.savedAIDesigns.map((d) => (d.aiDesignId === aiDesignId ? { ...d, ...patch } : d)),
        })),

    activeAIDesignId: null,
    setActiveAIDesignId: (id) => set({ activeAIDesignId: id, activeSubScenarioId: null }),

    activeSubScenarioId: null,
    setActiveSubScenarioId: (id) => set({ activeSubScenarioId: id }),

    addTreeCutSubScenario: (aiDesignId, sub) =>
        set((state) => ({
            savedAIDesigns: state.savedAIDesigns.map((d) =>
                d.aiDesignId === aiDesignId
                    ? {
                          ...d,
                          treeCutSubScenarios: [
                              ...(d.treeCutSubScenarios ?? []).filter((s) => s.id !== sub.id),
                              sub,
                          ],
                      }
                    : d,
            ),
        })),
    removeTreeCutSubScenario: (aiDesignId, subId, callback) => {
        let updatedDesigns: SavedAIDesign[] = [];
        set((state) => {
            updatedDesigns = state.savedAIDesigns
                .map((d) =>
                    d.aiDesignId === aiDesignId
                        ? {
                              ...d,
                              treeCutSubScenarios: (d.treeCutSubScenarios ?? []).filter((s) => s.id !== subId),
                          }
                        : d,
                )
                .filter((d) => (d.treeCutSubScenarios ?? []).length > 0);

            const activeDesignStillExists = updatedDesigns.some(
                (d) => d.aiDesignId === state.activeAIDesignId,
            );
            return {
                savedAIDesigns: updatedDesigns,
                activeSubScenarioId: state.activeSubScenarioId === subId ? null : state.activeSubScenarioId,
                activeAIDesignId: activeDesignStillExists ? state.activeAIDesignId : null,
            };
        });
        callback?.(updatedDesigns);
    },

    priceQuoteOverrides: null,
    setPriceQuoteOverrides: (v) => set({ priceQuoteOverrides: v ?? null }),

    systemDesign: null,
    setSystemDesign: (v) => set({ systemDesign: v ?? null }),
    systemDesignRefreshing: false,
    setSystemDesignRefreshing: (refreshing) => set({ systemDesignRefreshing: refreshing }),
    systemDesignRefreshTrigger: 0,
    setSystemDesignRefreshTrigger: (v) => set({ systemDesignRefreshTrigger: v }),
    
    priceQuoteDetails: null,
    setPriceQuoteDetails: (v) => set({ priceQuoteDetails: v ?? null }),

    systemDesignQuotesLoading: false,
    setSystemDesignQuotesLoading: (v) => set({ systemDesignQuotesLoading: v }),

    systemDesignLosses: null,
    setSystemDesignLosses: (v) => set({ systemDesignLosses: v ?? null }),

    designImage: null,
    setDesignImage: (v) => set({ designImage: v ?? null }),

    opportunityinfoMasterData: null,
    setOpportunityinfoMasterData: (data) => set({ opportunityinfoMasterData: data ?? null }),

    designHistory: null,
    setDesignHistory: (v) => set({ designHistory: v ?? null }),

    equipmentMasterData: null,
    setEquipmentMasterData: (data) => set({ equipmentMasterData: data ?? null }),

    batteryModelData: null,
    setBatteryModelData: (data) => set({ batteryModelData: data ?? null }),

    titleInfo: null,
    setTitleInfo: (info) => set({ titleInfo: info ?? null }),

    productTypeList: [],
    setProductTypeList: (list) => set({ productTypeList: list }),

    financePartnersList: [],
    setFinancePartnersList: (list) => set({ financePartnersList: list }),

    financePartnerSyncStatus: {},
    setFinancePartnerSyncStatus: (partner, status, productTypeName) => {
        const partnerKey = normalizeFinanceGridPartner(String(partner || '').trim());
        const productKey = String(productTypeName || '')
            .trim()
            .replace(/\s+only$/i, '');
        const key = productKey && partnerKey ? `${partnerKey}::${productKey}` : partnerKey;
        set((state) => {
            const defaults: PartnerSyncChecklist = {
                leadSyncAction: null,
                lead: false,
                leadSystem: false,
                utility: false,
                contact: false,
                titleCheck: false,
                creditCheck: false,
            };
            const existing = state.financePartnerSyncStatus[key] ?? {};
            return {
                financePartnerSyncStatus: {
                    ...state.financePartnerSyncStatus,
                    [key]: { ...defaults, ...existing, ...status },
                },
            };
        });
    },
    financePartnerSyncModalOpen: false,
    setFinancePartnerSyncModalOpen: (open) => set({ financePartnerSyncModalOpen: open }),

    leadSyncFormOpen: false,
    leadSyncFormAction: null,
    leadSyncFormPartnerName: null,
    leadSyncFormProductName: null,
    leadSyncFormHideCreateLead: false,
    setLeadSyncFormOpen: (open, action, partnerName, options) =>
        set({
            leadSyncFormOpen: open,
            leadSyncFormAction: action ?? null,
            leadSyncFormPartnerName: partnerName ?? null,
            leadSyncFormProductName: open ? (options?.productName ?? null) : null,
            leadSyncFormHideCreateLead: open ? !!options?.hideCreateLead : false,
        }),
    igsLeadSyncModalOpen: false,
    setIgsLeadSyncModalOpen: (open, productName) =>
        set({
            igsLeadSyncModalOpen: open,
            leadSyncFormProductName: open ? (productName ?? null) : null,
        }),

    creditCheckModalOpen: false,
    creditCheckPartnerName: null,
    setCreditCheckModalOpen: (open, partnerName) => set({ 
        creditCheckModalOpen: open, 
        creditCheckPartnerName: partnerName ?? null 
    }),

    financePartnerRawSyncStatuses: [],
    setFinancePartnerRawSyncStatuses: (statuses) => set({ financePartnerRawSyncStatuses: statuses }),

    contractCreationStatuses: [],
    setContractCreationStatuses: (statuses) => set({ contractCreationStatuses: statuses }),

    sfdcSyncErrors: {},
    setSfdcSyncError: (step, error) =>
        set((state) => {
            const next = { ...state.sfdcSyncErrors };
            if (error) {
                next[step] = error;
            } else {
                delete next[step];
            }
            return { sfdcSyncErrors: next };
        }),
    clearSfdcSyncErrors: () => set({ sfdcSyncErrors: {} }),

    isBackgroundSyncing: false,
    setIsBackgroundSyncing: (isSyncing) => set({ isBackgroundSyncing: isSyncing }),

    commerceHubProductsOpen: false,
    commerceHubQuotesOpen: false,
    commerceHubAgreementsOpen: false,
    commerceHubStripeOpen: false,
    pciLockOpportunityProducts: false,
    setPciLockOpportunityProducts: (locked) => set({ pciLockOpportunityProducts: locked }),
    setCommerceHubProductsOpen: (open, opts) =>
        set(
            open
                ? opts?.keepQuotesOpen
                    ? { commerceHubProductsOpen: true, commerceHubStripeOpen: false }
                    : {
                          commerceHubProductsOpen: true,
                          commerceHubQuotesOpen: false,
                          commerceHubAgreementsOpen: false,
                          commerceHubStripeOpen: false,
                      }
                : { commerceHubProductsOpen: false },
        ),
    setCommerceHubQuotesOpen: (open) =>
        set(
            open
                ? {
                      commerceHubQuotesOpen: true,
                      commerceHubProductsOpen: false,
                      commerceHubAgreementsOpen: false,
                      commerceHubStripeOpen: false,
                  }
                : { commerceHubQuotesOpen: false }
        ),
    setCommerceHubAgreementsOpen: (open) =>
        set(
            open
                ? {
                      commerceHubAgreementsOpen: true,
                      commerceHubProductsOpen: false,
                      commerceHubQuotesOpen: false,
                      commerceHubStripeOpen: false,
                  }
                : { commerceHubAgreementsOpen: false }
        ),
    setCommerceHubStripeOpen: (open) =>
        set(
            open
                ? {
                      commerceHubStripeOpen: true,
                      commerceHubProductsOpen: false,
                      commerceHubQuotesOpen: false,
                      commerceHubAgreementsOpen: false,
                  }
                : { commerceHubStripeOpen: false }
        ),

    dashboardContactsModalOpen: false,
    setDashboardContactsModalOpen: (open) => set({ dashboardContactsModalOpen: open }),
    dashboardContactsOpenNewContact: false,
    setDashboardContactsOpenNewContact: (open) => set({ dashboardContactsOpenNewContact: open }),
    contactsTitleSyncRevision: 0,
    notifyContactsTitleSynced: () =>
        set((state) => ({ contactsTitleSyncRevision: state.contactsTitleSyncRevision + 1 })),
    dashboardSalesPersonModalOpen: false,
    setDashboardSalesPersonModalOpen: (open) => set({ dashboardSalesPersonModalOpen: open }),
    salesRepChecklistModalOpen: false,
    setSalesRepChecklistModalOpen: (open) => set({ salesRepChecklistModalOpen: open }),
    salesRepChecklistProgress: null,
    setSalesRepChecklistProgress: (progress) => set({ salesRepChecklistProgress: progress }),
    checklistResponseTone: null,
    setChecklistResponseTone: (tone) => set({ checklistResponseTone: tone ?? null }),
    documentsModalOpen: false,
    setDocumentsModalOpen: (open) => set({ documentsModalOpen: open }),

    hoverJobId: null,
    setHoverJobId: (id) =>
        set({
            hoverJobId:
                id == null || String(id).trim() === '' ? null : String(id).trim(),
        }),
    selectedHoverEstimate: null,
    setSelectedHoverEstimate: (v) => set({ selectedHoverEstimate: v ?? null }),
    hoverPendingGenerateQuote: null,
    setHoverPendingGenerateQuote: (v) => set({ hoverPendingGenerateQuote: v ?? null }),

    roofDetails: null,
    updateRoofDetails: (data) => set({ roofDetails: data }),
    rrDetails: null,
    updateRRDetails: (data) => set({ rrDetails: data }),
    batteryDetails: null,
    updateBatteryDetails: (data) => set({ batteryDetails: data }),
    quotesRefreshTrigger: 0,
    setQuotesRefreshTrigger: (v) => set({ quotesRefreshTrigger: v }),

    pricingNetworkRefreshTrigger: 0,
    setPricingNetworkRefreshTrigger: (v) => set({ pricingNetworkRefreshTrigger: v }),

    pricingProductsEnabled: { ...DEFAULT_PRICING_PRODUCTS_ENABLED },
    togglePricingProductEnabled: (product) =>
        set((state) => ({
            pricingProductsEnabled: {
                ...state.pricingProductsEnabled,
                [product]: !state.pricingProductsEnabled[product],
            },
            pricingNetworkRefreshTrigger: Date.now(),
        })),

    pricingDesignFingerprint: null,

    pricingModalFetchTrigger: null,
    setPricingModalFetchTrigger: (v) => set({ pricingModalFetchTrigger: v ?? null }),

    agreementsRefreshTrigger: 0,
    setAgreementsRefreshTrigger: (v) => set({ agreementsRefreshTrigger: v }),

    agreementsPendingExpandQuoteId: null,
    setAgreementsPendingExpandQuoteId: (id) => set({ agreementsPendingExpandQuoteId: id ?? null }),

    financeSelectionRefreshTrigger: 0,
    setFinanceSelectionRefreshTrigger: (v) => set({ financeSelectionRefreshTrigger: v }),

    onebuttonCapabilities: null,
    fetchOnebuttonCapabilities: async (force) => {
        const key = 'onebuttonCapabilities';

        if (!force) {
            const fromStore = useOneButtonStore.getState().onebuttonCapabilities;
            if (fromStore) return;
            const fromSession = sessionStorage.getItem(key);
            if (fromSession) {
                try {
                    set({ onebuttonCapabilities: JSON.parse(fromSession) as OneButtonUserCapabilityResponse });
                    return;
                } catch {
                    sessionStorage.removeItem(key);
                }
            }
        }

        try {
            const data = await oneButtonCapabilityService.getUserCapability();
            set({ onebuttonCapabilities: data });
            sessionStorage.setItem(key, JSON.stringify(data));
        } catch {
            // no-op (capabilities stay null); caller can decide how to handle failures if needed
        }
    },
}));